import { useTranslation } from 'react-i18next';
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Icon,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ChartTooltip,
    ResponsiveContainer,
} from '../../../../base';
import {
    IClientSourceOfHireChartData,
    ISourceOfHireStageBar,
    getSourceOfHireStageBars,
    formatSourceOfHireAmount,
    exportSourceOfHireStagesToExcel,
} from '../../../../common';
import { Directions } from '../../../../common/consts/system.consts';
import {
    StyledBarCard,
    StyledBarCardHeader,
    StyledBarCardBody,
    StyledBarEmptyState,
} from './BarView.styles';

interface ISourceOfHireStagesBarCardProps {
    chartData: IClientSourceOfHireChartData;
    sourceTitle: string | null;
}

interface IStageBarShapeProps {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    payload?: { value: number; total: number };
}

interface IStageTooltipProps {
    active?: boolean;
    payload?: { payload: { value: number; total: number } }[];
}

const SourceOfHireStagesBarCard = ({
    chartData,
    sourceTitle,
}: ISourceOfHireStagesBarCardProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isRtl = theme.direction === Directions.rtl;

    const bars: ISourceOfHireStageBar[] = sourceTitle
        ? getSourceOfHireStageBars(chartData, sourceTitle, t)
        : [];
    const maxTotal = Math.max(0, ...bars.map((bar) => bar.total));
    const hasData = maxTotal > 0;

    const StageBarShape = (props: IStageBarShapeProps) => {
        const { x = 0, y = 0, width = 0, height = 0, payload } = props;
        if (!payload) return null;

        const trackHeight =
            payload.value > 0 ? (payload.total / payload.value) * height : 0;
        const trackY = y + height - trackHeight;

        return (
            <g>
                <rect
                    x={x}
                    y={trackY}
                    width={width}
                    height={Math.max(0, trackHeight)}
                    rx={5}
                    ry={5}
                    fill={theme.palette.primary.main}
                    opacity={0.2}
                />
                <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    rx={5}
                    ry={5}
                    fill={theme.palette.primary.main}
                />
            </g>
        );
    };

    const renderTooltipContent = ({ active, payload }: IStageTooltipProps) => {
        if (!active || !payload?.length) return null;
        const { value, total } = payload[0].payload;
        return (
            <Box
                sx={{
                    backgroundColor: theme.palette.grey[600],
                    color: theme.palette.common.white,
                    padding: theme.spacing(0.5, 1.5),
                    borderRadius: 1.5,
                    ...theme.typography.caption,
                }}
            >
                {`${Math.round(value)}/${Math.round(total)}`}
            </Box>
        );
    };

    return (
        <StyledBarCard>
            <StyledBarCardHeader container sx={{ justifyContent: 'flex-end' }}>
                {hasData && sourceTitle && (
                    <IconButton
                        size="small"
                        onClick={() =>
                            exportSourceOfHireStagesToExcel(
                                bars,
                                sourceTitle,
                                t
                            )
                        }
                    >
                        <Icon name="FileDownloadOutlinedIcon" size={16} />
                    </IconButton>
                )}
            </StyledBarCardHeader>
            <StyledBarCardBody>
                {hasData ? (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                            data={bars}
                            margin={{
                                top: 40,
                                right: isRtl ? 5 : 40,
                                left: isRtl ? 40 : 5,
                            }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke={theme.palette.grey[300]}
                            />
                            <XAxis
                                dataKey="name"
                                reversed={isRtl}
                                tick={{
                                    fill: theme.palette.text.primary,
                                    fontSize: 12,
                                }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                domain={[0, maxTotal]}
                                orientation={isRtl ? 'right' : 'left'}
                                tickFormatter={(value: number) =>
                                    formatSourceOfHireAmount(value, false)
                                }
                                tick={{
                                    fill: theme.palette.grey[600],
                                    fontSize: 14,
                                    textAnchor: 'end',
                                }}
                                axisLine={false}
                                tickLine={false}
                                width={60}
                            />
                            <ChartTooltip
                                content={renderTooltipContent as never}
                                cursor={false}
                            />
                            <Bar
                                dataKey="value"
                                barSize={58}
                                shape={<StageBarShape />}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <StyledBarEmptyState>
                        <Icon
                            name="NoTrackableDataIconBig"
                            size={72}
                            color="primary"
                        />
                        <Typography variant="h6">
                            {t(
                                'reportsPage.sourceOfHireReport.noMetricDataTitle'
                            )}
                        </Typography>
                        <Typography
                            variant="body2"
                            color={theme.palette.grey[600]}
                        >
                            {t(
                                'reportsPage.sourceOfHireReport.noMetricDataDescription'
                            )}
                        </Typography>
                    </StyledBarEmptyState>
                )}
            </StyledBarCardBody>
        </StyledBarCard>
    );
};

export default SourceOfHireStagesBarCard;
